import React, { useState } from 'react';
import { Grid, Collapse, Box, Button, Typography, Alert } from '@mui/material';
import {
    Input,
    Select,
    InformationAlert,
} from "../../Common";
import Enums from '../../../enums';
import { TranslateW } from "../../../Utils/Languages/Translate";

const RepairNotes = ({
    formState,
    setGlobalState,
    dropdownOptions,
    requiredFieldsMissing,
    serviceEventSteps = [],
    setServiceEventSteps,
    requiredFieldsRef,
    setRequiredFields,
}) => {
    const [warning, setWarning] = useState(false);
    const isNestedProperty = true;
    const renderServiceActionField = formState.workItemTypeId === Enums.SE.WorkItemType.ServiceOrLeakRepair;
    const showRepairOutcome = renderServiceActionField && formState.initialLeakInspection?.wasLeakFound === true;

    // Cascade-clear IVT/FVT form state when Repair Outcome becomes "No Repair Attempted" (#8775,
    // bug #15369). Without this the data is only hidden and reappears (e.g. Verification Result =
    // "Fail") when switching back to a repair outcome. Mirrors RepairDetails.deleteIvtAndFvtRelatedValues.
    const clearIvtAndFvtRelatedValues = () => {
        setGlobalState("initialVerificationLeakInspection.inspectionDate", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakRepairDispositionTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationCategoryId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.faultCauseTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.estimatedLeakAmount", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.notes", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.inspectionDate", null, isNestedProperty);
        setGlobalState("dateOfFollowUpService", null);

        // Remove the Follow Up Verification step if it was added.
        if (setServiceEventSteps) {
            const followUpIndex = serviceEventSteps.map(s => s.props.id).indexOf('FollowUpVerificationService');
            if (followUpIndex !== -1) {
                const newSteps = [...serviceEventSteps];
                newSteps.splice(followUpIndex, 1);
                setServiceEventSteps(newSteps);
            }
        }

        // IVT/FVT are not required under "No Repair Attempted".
        if (requiredFieldsRef && setRequiredFields) {
            const resetRequiredFields = {
                ...requiredFieldsRef.current,
                InitialVerificationTest: [],
                FollowUpVerificationService: [],
            };
            requiredFieldsRef.current = resetRequiredFields;
            setRequiredFields(resetRequiredFields);
        }
    };

    const handleRepairOutcomeSelect = (outcomeId) => {
        setGlobalState("repairOutcomeTypeId", outcomeId);
        if (outcomeId === Enums.SE.RepairOutcomeType.NoRepairAttempted) {
            clearIvtAndFvtRelatedValues();
            return;
        }
        // Switching back to a repair outcome re-arms IVT validation (mirrors RepairDetails).
        if (
            (outcomeId === Enums.SE.RepairOutcomeType.LeakRepaired
                || outcomeId === Enums.SE.RepairOutcomeType.LeakNotRepaired)
            && requiredFieldsRef && setRequiredFields
        ) {
            const newRequiredFields = {
                ...requiredFieldsRef.current,
                InitialVerificationTest: ["leakDetectionMethodId", "wasLeakFound"],
            };
            requiredFieldsRef.current = newRequiredFields;
            setRequiredFields(newRequiredFields);
        }
    };
    const repairOutcomeLabelSx = {
        color: '#000000',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        lineHeight: 1.2,
        mb: 1.5,
        textTransform: 'uppercase',
    };
    const getRepairOutcomeButtonSx = (isSelected) => ({
        flex: 1,
        minHeight: '38px',
        borderRadius: '4px',
        border: `1px solid ${isSelected ? '#0E97B7' : '#D0D5DD'}`,
        backgroundColor: '#FFFFFF',
        color: isSelected ? '#0E97B7' : '#98A2B3',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.2,
        textTransform: 'none',
        justifyContent: 'center',
        px: 1.5,
        '&:hover': {
            borderColor: '#0E97B7',
            backgroundColor: '#FFFFFF',
        },
    });

    return (
        <Grid container spacing={4}>
            {renderServiceActionField && (
                <Grid item xs={12} md={6}>
                    <Select
                        required
                        label={TranslateW("generalService.service-action")}
                        value={formState.serviceActionId}
                        onChange={e => setGlobalState("serviceActionId", e.target.value)}
                        options={dropdownOptions.serviceActions}
                        error={requiredFieldsMissing.serviceActionId}
                        disabled={!formState.workItemTypeId}
                    />
                </Grid>
            )}
            <Grid item xs={12} md={12}>
                <Input
                    fullWidth
                    multiLine
                    label={TranslateW('generalService.parts-required') }
                    value={formState.partsRequired}
                    onChange={e => setGlobalState("partsRequired", e.target.value)}
                />
            </Grid>
            <Grid item xs={12} md={12}>
                <Input
                    fullWidth
                    multiLine
                    required
                    label={TranslateW("leakRepair.repair-notes") }
                    value={formState.repairNotes}
                    onChange={e => setGlobalState("repairNotes", e.target.value)}
                    onFocus={() => setWarning(true)}
                    error={requiredFieldsMissing.repairNotes}
                />
            </Grid>
            <Grid item xs={12} md={12} lg={12} sx={{ display: warning ? 'block' : 'none' }}>
                <Collapse in={warning}>
                    <Grid item xs={12} md={12} lg={12}>
                        <InformationAlert
                            title={TranslateW('Information')}
                            message={TranslateW("ExactLeakDescription") }
                            onClose={() => setWarning(false)}
                        />
                    </Grid>
                </Collapse>
            </Grid>
            {showRepairOutcome && (
                <Grid item xs={12} md={12} lg={6}>
                    <Typography sx={repairOutcomeLabelSx}>
                        {TranslateW("repairOutcome.label")} <span style={{ color: '#F04438' }}>*</span>
                    </Typography>
                    <Box display="flex" gap={1.5}>
                        {dropdownOptions.repairOutcomeTypes?.map((option) => {
                            const isSelected = formState.repairOutcomeTypeId === option.id;
                            return (
                                <Button
                                    key={option.id}
                                    variant="outlined"
                                    onClick={() => handleRepairOutcomeSelect(option.id)}
                                    sx={getRepairOutcomeButtonSx(isSelected)}
                                >
                                    {option.name}
                                </Button>
                            );
                        })}
                    </Box>
                    {requiredFieldsMissing.repairOutcomeTypeId && (
                        <Typography sx={{ color: '#D32F2F', fontSize: '12px', mt: 1 }}>
                            This field is required
                        </Typography>
                    )}
                </Grid>
            )}
            {showRepairOutcome && formState.repairOutcomeTypeId && (
                <Grid item xs={12} md={12} lg={12}>
                    <Alert severity="info">
                        {TranslateW(`repairOutcome.Guidance.${formState.repairOutcomeTypeId}`)}
                    </Alert>
                </Grid>
            )}
        </Grid>
    );
};

export default RepairNotes;
